import { RefObject, useCallback, useEffect, useRef } from "react";

import { waitForSnowplowAnalyticsReady } from "../helpers/analyticsHelpers";
import { usePlayerStore } from "../store/usePlayerStore";

import { useSnowplowEnabled } from "./useSnowplowEnabled";

type EventQueue = [string, Record<string, unknown>][];

const SESSION_STARTED_EVENT = "startMediaTracking";

const eventAllowedBeforeSessionStart = (event: string) => {
  // These events are not tracked before session start, they are unnecessary and can cause problems
  return ![
    "endMediaTracking",
    "trackMediaSeekEnd",
    "trackMediaSeekStart",
    "trackMediaEnd"
  ].includes(event);
};

/**
 * Events queue system:
 * There is 2 event queues.
 *
 * 1. Queue for the events before snowplow is ready:
 * window.snowplowReady is a promise, that resolves when the embedding service (Ukko/Areena/etc) has been loaded the snowplow script
 * It's possible, that this promise never resolves (because of adblocker or something) or it takes time (slow network)
 * Player does not wait for the snowplowReady resolving, but it queues the events and sends them after promise resolves
 *
 * 2. Queue for the events before session is ready:
 * The tracking session event "startMediaTracking" will be sent when video starts to play
 * Some events, like audio and text track changes, quality change etc. might happen before the actual session start
 * These events are queued and send after session has been started
 *
 */

type SendSnopwlowEvent = (
  event: string,
  data?: Record<string, unknown>
) => void;

export const useSendSnowplowEvent = ({
  sessionId
}: {
  sessionId: RefObject<string | undefined>;
}): SendSnopwlowEvent => {
  const { getState } = usePlayerStore();
  const snowplowEnabled = useSnowplowEnabled();

  const snowplowReady = useRef(false);

  const eventsBeforeSnowplowReady = useRef<EventQueue>([]);
  const eventsBeforeSessionReady = useRef<EventQueue>([]);

  const sendEvent = useCallback(
    (event: string, data: Record<string, unknown> = {}) => {
      if (snowplowEnabled && !getState().isCasting) {
        window.yleSnowplow(event, {
          ...data,
          id: sessionId.current
        });
      }
    },
    [getState, sessionId, snowplowEnabled]
  );

  const handleQueuedEventsAfterSessionStart = useCallback(() => {
    eventsBeforeSessionReady.current.forEach(([e, d]) => {
      sendEvent(e, d);
    });
    eventsBeforeSessionReady.current = [];
  }, [sendEvent]);

  const handleEventAfterSessionStart = useCallback(
    (event: string, data: Record<string, unknown> = {}) => {
      if (snowplowReady.current) {
        sendEvent(event, data);

        if (event === SESSION_STARTED_EVENT) {
          // New session started, run possible queued events
          handleQueuedEventsAfterSessionStart();
        }
      } else {
        eventsBeforeSnowplowReady.current.push([event, data]);
      }
    },
    [handleQueuedEventsAfterSessionStart, sendEvent]
  );

  const sendEventOrAddToQueue: SendSnopwlowEvent = useCallback(
    (event, data = {}) => {
      if (!snowplowEnabled) {
        return;
      }

      if (sessionId.current) {
        handleEventAfterSessionStart(event, data);
      } else if (eventAllowedBeforeSessionStart(event)) {
        eventsBeforeSessionReady.current.push([event, data]);
      }
    },
    [handleEventAfterSessionStart, sessionId, snowplowEnabled]
  );

  useEffect(() => {
    if (snowplowEnabled) {
      waitForSnowplowAnalyticsReady()
        .then(() => {
          snowplowReady.current = true;
          eventsBeforeSnowplowReady.current.forEach(([event, data]) => {
            // Snowplow is ready, send the events queued before session start
            sendEventOrAddToQueue(event, data);
          });
          eventsBeforeSnowplowReady.current = [];
        })
        .catch(err => console.error("Failed to load Snowplow script", err));
    }
  }, [sendEventOrAddToQueue, sessionId, snowplowEnabled]);

  return sendEventOrAddToQueue;
};
